import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { type User } from '@/types';
import { Check, Loader2, User as UserIcon, X } from 'lucide-react';
import { useState } from 'react';

interface InlineAssignedUserCellProps {
    assignedUsers: User[];
    allUsers: User[];
    onUpdate?: (userIds: string[]) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

export function InlineAssignedUserCell({ assignedUsers, allUsers, onUpdate, className, readOnly = false }: InlineAssignedUserCellProps) {
    const { t } = useTranslations();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set(assignedUsers.map((u) => u.id)));
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');

    const handleToggleUser = (userId: string) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!onUpdate) return;

        const currentIds = new Set(assignedUsers.map((u) => u.id));
        const newIds = Array.from(selectedUserIds);

        // Check if anything changed
        if (currentIds.size === newIds.length && newIds.every((id) => currentIds.has(id))) {
            setIsDialogOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(newIds);
            setIsDialogOpen(false);
            setSearch('');
        } catch (error) {
            console.error('Failed to update assigned users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setSelectedUserIds(new Set(assignedUsers.map((u) => u.id)));
        setSearch('');
        setIsDialogOpen(false);
    };

    const displayUsers = search
        ? allUsers.filter(
              (user) =>
                  `${user.first_name} ${user.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
                  user.email?.toLowerCase().includes(search.toLowerCase()) ||
                  user.position?.toLowerCase().includes(search.toLowerCase()),
          )
        : allUsers;

    // Read-only mode: just display the users
    if (readOnly || !onUpdate) {
        if (assignedUsers.length === 0) {
            return (
                <div className={cn('text-muted-foreground flex items-center gap-2', className)}>
                    <UserIcon className="h-4 w-4" />
                    <span className="text-xs italic">{t('companies.index.unassigned', 'Unassigned')}</span>
                </div>
            );
        }

        return (
            <div className={cn('flex items-center gap-2', className)}>
                <div className="flex -space-x-1.5">
                    {assignedUsers.slice(0, 3).map((user, index) => (
                        <Avatar
                            key={user.id}
                            className="border-border bg-muted h-6 w-6 border text-xs"
                            style={{ zIndex: assignedUsers.length - index }}
                        >
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback className="text-[10px]">
                                {user.first_name?.[0]}
                                {user.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                    ))}
                </div>
                {assignedUsers.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                        +{assignedUsers.length - 3}
                    </Badge>
                )}
            </div>
        );
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsDialogOpen(true);
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center gap-2', className)}
                >
                    {assignedUsers.length === 0 ? (
                        <>
                            <UserIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                            <span className="text-muted-foreground text-sm italic">{t('companies.index.unassigned', 'Unassigned')}</span>
                        </>
                    ) : (
                        <>
                            <div className="flex -space-x-1.5">
                                {assignedUsers.slice(0, 2).map((user, index) => (
                                    <Avatar
                                        key={user.id}
                                        className="border-border bg-muted h-6 w-6 border text-xs"
                                        style={{ zIndex: assignedUsers.length - index }}
                                    >
                                        <AvatarImage src={user.avatar} />
                                        <AvatarFallback className="text-[10px]">
                                            {user.first_name?.[0]}
                                            {user.last_name?.[0]}
                                        </AvatarFallback>
                                    </Avatar>
                                ))}
                            </div>
                            {assignedUsers.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                    +{assignedUsers.length - 2}
                                </Badge>
                            )}
                        </>
                    )}
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t('companies.index.columns.assigned_to', 'Assigned Users')}</DialogTitle>
                </DialogHeader>
                <div className="flex max-h-[70vh] min-h-0 flex-col gap-4">
                    <Command shouldFilter={false} className="min-h-0 flex-1">
                        <CommandInput placeholder={t('companies.index.search_users', 'Search users...')} value={search} onValueChange={setSearch} />
                        <CommandList className="max-h-[45vh] overflow-y-auto">
                            <CommandEmpty>{t('companies.index.no_users_found', 'No users found.')}</CommandEmpty>
                            <CommandGroup>
                                {displayUsers.map((user) => {
                                    const isSelected = selectedUserIds.has(user.id);
                                    return (
                                        <CommandItem key={user.id} onSelect={() => handleToggleUser(user.id)} className="cursor-pointer">
                                            <div className="flex w-full items-center gap-3">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onCheckedChange={() => handleToggleUser(user.id)}
                                                    className="h-4 w-4"
                                                />
                                                <Avatar className="h-8 w-8 shrink-0">
                                                    <AvatarImage src={user.avatar} />
                                                    <AvatarFallback className="text-xs">
                                                        {user.first_name?.[0]}
                                                        {user.last_name?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex min-w-0 flex-1 flex-col">
                                                    <span className="truncate text-sm">
                                                        {user.first_name} {user.last_name}
                                                    </span>
                                                    {user.position && <span className="text-muted-foreground truncate text-xs">{user.position}</span>}
                                                </div>
                                                {isSelected && <Check className="ml-auto h-4 w-4 shrink-0" />}
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                    <div className="flex shrink-0 justify-end gap-2 border-t pt-2">
                        <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
                            <X className="mr-2 h-4 w-4" />
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={isLoading}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                            {t('common.save', 'Save')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
